import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Show,
  useRecordContext,
  useGetOne,
  useGetList,
  useGetMany,
  useNotify,
  useRefresh,
  RecordContextProvider,
} from 'react-admin';
import { Drawer, IconButton, useMediaQuery, Theme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { User, FileText, Globe, Zap, Pencil, UserPlus, Menu as MenuLucide } from 'lucide-react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { supabase } from '../lib/supabaseClient';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { ContactActivityFeed } from '../components/ContactActivityFeed';
import { QualifyLeadDialog } from './QualifyLeadDialog';
import { DISQUALIFY_REASONS } from './disqualifyReasons';
import { ScheduledSmsRow } from './ScheduledSmsRow';
import { MessageInputBar } from '../funerals/components/MessageInputBar';
import { MessageDraftProvider } from '../funerals/contexts/MessageDraftContext';
import { LeadOwnerControls, LeadShowToolBar } from './LeadShowToolBar';
import { LeadResetStageButton } from './LeadResetStageButton';
import { ContactCard } from '../components/ContactCard';
import { LeadLinkButton, LeadLinkedBanner } from '../caller-links/LeadLinkControls';
import { CallLinkProvider } from '../caller-links/CallLinkContext';
import { getClientColor } from '../funerals/components/ClientFilterBar';
import { CustomerEvent } from '../types';
import { getToken, getRefreshToken } from '../authentication/authProvider';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField as MuiTextField,
  Checkbox,
  Menu,
  MenuItem,
  Chip,
  ButtonGroup,
} from '@mui/material';

const API_URL = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;

// ─── Reusable label-value row ────────────────────────────────────────────────

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-1.5">
      <span className="text-[13px] text-muted-foreground leading-5 shrink-0">{label}</span>
      <div className="text-[13px] text-foreground leading-5 min-w-0 text-right">{children}</div>
    </div>
  );
}

// ─── Inline select ───────────────────────────────────────────────────────────

function InlineSelect({
  value,
  options,
  placeholder,
  onChange,
}: {
  value: string | null;
  options: { value: string; label: string }[];
  placeholder: string;
  onChange: (val: string) => void;
}) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="text-[13px] bg-transparent border border-border rounded px-1.5 py-0.5 text-right text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

// ─── Stage chip ──────────────────────────────────────────────────────────────

function StageChip({ stage }: { stage: string }) {
  const colorMap: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
    qualified: 'success',
    closed_won: 'success',
    closed_lost: 'error',
    disqualified: 'error',
    spam: 'error',
    spoken: 'info',
    callback_needed: 'warning',
    new: 'default',
  };
  const labelMap: Record<string, string> = {
    new: 'New',
    callback_needed: 'Callback Needed',
    spoken: 'Spoken',
    qualified: 'Qualified',
    disqualified: 'Disqualified',
    spam: 'Spam',
    closed_won: 'Won',
    closed_lost: 'Lost',
  };
  return (
    <Chip
      label={labelMap[stage] || stage}
      size="small"
      color={colorMap[stage] || 'default'}
    />
  );
}

// ─── Close Lost button ───────────────────────────────────────────────────────

// `open` / `onOpenChange` let the Reset stage dialog hand off to this one.
function LeadCloseLostButton({
  open: openProp,
  onOpenChange,
}: { open?: boolean; onOpenChange?: (open: boolean) => void } = {}) {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = onOpenChange ?? setOpenState;
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  if (!record) return null;
  if (record.stage !== 'qualified') return null;

  const handleClose = () => {
    setOpen(false);
    setReason('');
    setNotes('');
  };

  const handleConfirm = async () => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({
          stage: 'closed_lost',
          closed_lost_reason: reason,
          closed_lost_notes: notes.trim() || null,
          closed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', record.id);
      if (error) throw error;
      notify('Lead closed as lost', { type: 'success' });
      handleClose();
      refresh();
    } catch (e: any) {
      notify(e.message || 'Failed to close lead', { type: 'warning' });
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-[13px] text-red-600 hover:text-red-700 font-medium border border-red-300 rounded px-2.5 py-1 hover:bg-red-50 transition-colors"
      >
        Close Lost
      </button>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Close Lost</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" sx={{ mt: 1, width: '100%' }}>
            <FormLabel component="legend">Reason *</FormLabel>
            <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)}>
              <FormControlLabel value="price" control={<Radio />} label="Found lower price" />
              <FormControlLabel value="competitor" control={<Radio />} label="Went with competitor" />
              <FormControlLabel value="traditional" control={<Radio />} label="Traditional Funeral Home" />
              <FormControlLabel value="no_reply" control={<Radio />} label="No Reply" />
              <FormControlLabel value="out_of_area" control={<Radio />} label="Out of Service Area" />
              <FormControlLabel value="duplicate" control={<Radio />} label="Duplicate" />
              <FormControlLabel value="other" control={<Radio />} label="Other" />
            </RadioGroup>
          </FormControl>
          <MuiTextField
            label={reason === 'other' ? 'Notes *' : 'Notes'}
            multiline
            minRows={2}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!reason || (reason === 'other' && !notes.trim())}
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Disqualify button ───────────────────────────────────────────────────────

function LeadDisqualifyButton() {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  if (!record) return null;
  if (['qualified', 'disqualified', 'closed_won', 'closed_lost'].includes(record.stage)) return null;

  const handleClose = () => {
    setOpen(false);
    setReason('');
    setNotes('');
  };

  const handleConfirm = async () => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({
          stage: 'disqualified',
          disqualified_at: new Date().toISOString(),
          disqualified_reason: notes.trim() ? `${reason}: ${notes.trim()}` : reason,
          qualification_status: 'unqualified',
          updated_at: new Date().toISOString(),
        })
        .eq('id', record.id);
      if (error) throw error;
      notify('Lead disqualified', { type: 'success' });
      handleClose();
      refresh();
    } catch (e: any) {
      notify(e.message || 'Failed to disqualify lead', { type: 'warning' });
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-[13px] text-orange-600 hover:text-orange-700 font-medium border border-orange-300 rounded px-2.5 py-1 hover:bg-orange-50 transition-colors"
      >
        Disqualify
      </button>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Disqualify Lead</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" sx={{ mt: 1, width: '100%' }}>
            <FormLabel component="legend">Reason *</FormLabel>
            <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)}>
              {DISQUALIFY_REASONS.map(([value, label]) => (
                <FormControlLabel key={value} value={value} control={<Radio />} label={label} />
              ))}
            </RadioGroup>
          </FormControl>
          <MuiTextField
            label={reason === 'other' ? 'Notes *' : 'Notes'}
            multiline
            minRows={2}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!reason || (reason === 'other' && !notes.trim())}
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Linked Contacts List ────────────────────────────────────────────────────

function LinkedContactsList({ quoteId }: { quoteId: string }) {
  const { data: quoteContacts } = useGetList(
    'quote_contacts',
    { filter: { quote_id: quoteId }, pagination: { page: 1, perPage: 50 }, sort: { field: 'created_at', order: 'ASC' } },
    { enabled: !!quoteId },
  );

  if (!quoteContacts || quoteContacts.length === 0) {
    return <span className="text-[13px] text-muted-foreground">No additional contacts</span>;
  }

  return (
    <div className="space-y-2">
      {quoteContacts.map((qc) => (
        <LinkedContactRow key={qc.id} clientId={qc.client_id} role={qc.role} />
      ))}
    </div>
  );
}

function LinkedContactRow({ clientId, role }: { clientId: number; role?: string }) {
  const { data: contact } = useGetOne('clients', { id: clientId }, { enabled: !!clientId });
  const leadRecord = useRecordContext();
  const leadId = leadRecord?.id ? String(leadRecord.id) : undefined;
  const notify = useNotify();
  const refresh = useRefresh();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', primary_phone: '', primary_email: '' });

  if (!contact) return null;

  const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.name || '—';

  const startEdit = () => {
    setForm({
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      primary_phone: contact.phone_number || '',
      primary_email: contact.email || '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      const displayName = [form.first_name.trim(), form.last_name.trim()].filter(Boolean).join(' ');
      const { error } = await supabase
        .from('clients')
        .update({
          first_name: form.first_name.trim() || null,
          last_name: form.last_name.trim() || null,
          display_name: displayName || null,
          phone_number: form.primary_phone.trim() || null,
          email: form.primary_email.trim().toLowerCase() || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', clientId);
      if (error) throw error;
      notify('Contact updated', { type: 'success' });
      setEditing(false);
      refresh();
    } catch (e: any) {
      notify(e.message || 'Failed to update', { type: 'warning' });
    }
  };

  if (editing) {
    return (
      <div className="border border-border rounded px-2.5 py-1.5 text-[13px] space-y-1.5">
        <div className="flex gap-1.5">
          <input className="border border-border rounded px-1.5 py-0.5 flex-1 text-[13px] bg-background" placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          <input className="border border-border rounded px-1.5 py-0.5 flex-1 text-[13px] bg-background" placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
        </div>
        <input className="border border-border rounded px-1.5 py-0.5 w-full text-[13px] bg-background" placeholder="Phone" value={form.primary_phone} onChange={(e) => setForm({ ...form, primary_phone: e.target.value })} />
        <input className="border border-border rounded px-1.5 py-0.5 w-full text-[13px] bg-background" placeholder="Email" value={form.primary_email} onChange={(e) => setForm({ ...form, primary_email: e.target.value })} />
        <div className="flex items-center gap-1.5 pt-0.5">
          <button onClick={saveEdit} className="text-[12px] text-white bg-primary hover:bg-primary/90 rounded px-2 py-0.5 font-medium">Save</button>
          <button onClick={() => setEditing(false)} className="text-[12px] text-muted-foreground hover:text-foreground">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <ContactCard
      contact={{
        name,
        role,
        phone: contact.phone_number,
        email: contact.email,
        clientId,
      }}
      context={{ leadId: leadId ? String(leadId) : undefined }}
      renderName={() => (
        <button type="button" onClick={startEdit} className="font-medium hover:underline" title="Edit contact">
          {name}
        </button>
      )}
      renderEdit={() => (
        <button
          type="button"
          onClick={startEdit}
          className="inline-flex items-center gap-1 px-2 h-7 rounded-md text-[12px] text-muted-foreground hover:text-primary hover:bg-muted"
          title="Edit contact"
        >
          <Pencil className="w-4 h-4" /> Edit
        </button>
      )}
    />
  );
}

// ─── Add Contact Dialog ──────────────────────────────────────────────────────

function AddContactDialog({
  open,
  onClose,
  quoteId,
  leadId,
}: {
  open: boolean;
  onClose: () => void;
  quoteId: string;
  leadId: string;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [submitting, setSubmitting] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [relationship, setRelationship] = useState('');
  const [sendEmail, setSendEmail] = useState(true);

  const handleOpen = () => {
    setFirstName('');
    setLastName('');
    setPhone('');
    setEmail('');
    setRelationship('');
    setSendEmail(true);
  };

  const handleSubmit = async () => {
    if (!firstName.trim()) {
      notify('First name is required', { type: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      // 1. Create contact
      const displayName = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
      const { data: newContact, error: contactError } = await supabase
        .from('clients')
        .insert({
          first_name: firstName.trim(),
          last_name: lastName.trim() || null,
          display_name: displayName,
          phone_number: phone.trim() || null,
          email: email.trim().toLowerCase() || null,
        })
        .select('id')
        .single();
      if (contactError) throw contactError;

      // 2. Link to quote
      const { error: linkError } = await supabase
        .from('quote_contacts')
        .insert({
          quote_id: quoteId,
          client_id: newContact.id,
          role: relationship.trim() || null,
        });
      if (linkError) throw linkError;

      // 3. Optionally send quote email
      if (sendEmail && email.trim()) {
        try {
          const token = await getToken();
          await fetch(`${baseUrl}/leads/send-quote-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              'Refresh-Token': await getRefreshToken(),
            },
            body: JSON.stringify({
              quoteId,
              leadId,
              clientId: newContact.id,
              email: email.trim().toLowerCase(),
              clientName: firstName.trim(),
            }),
          });
        } catch {
          notify('Contact added but email failed to send', { type: 'warning' });
        }
      }

      notify(`Contact ${displayName} added${sendEmail && email.trim() ? ' and email sent' : ''}`, { type: 'success' });
      onClose();
      refresh();
    } catch (e: any) {
      notify(e.message || 'Failed to add contact', { type: 'warning' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth TransitionProps={{ onEnter: handleOpen }}>
      <DialogTitle>Add Contact</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <MuiTextField label="First Name *" value={firstName} onChange={(e) => setFirstName(e.target.value)} size="small" fullWidth />
          <MuiTextField label="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} size="small" fullWidth />
        </div>
        <MuiTextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} size="small" fullWidth />
        <MuiTextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} size="small" fullWidth />
        <MuiTextField label="Relationship" value={relationship} onChange={(e) => setRelationship(e.target.value)} size="small" fullWidth placeholder="e.g. Brother, Daughter, Spouse" />
        <FormControlLabel
          control={<Checkbox checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} disabled={!email.trim()} />}
          label="Send quote email"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting || !firstName.trim()}>
          {submitting ? 'Adding...' : 'Add Contact'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Sidebar ─────────────────────────────────────────────────────────────────

function LeadShowSidebar() {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [qualifyOpen, setQualifyOpen] = useState(false);
  const [closeLostOpen, setCloseLostOpen] = useState(false);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [contactForm, setContactForm] = useState({ first_name: '', last_name: '', primary_phone: '', primary_email: '' });
  const [sidebarKey, setSidebarKey] = useState(0);
  const refreshSidebar = () => { refresh(); setSidebarKey((k) => k + 1); };

  const { data: contact } = useGetOne(
    'clients',
    { id: record?.client_id },
    { enabled: !!record?.client_id },
  );

  const { data: quotes } = useGetList(
    'quotes',
    {
      filter: { lead_id: record?.id },
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'created_at', order: 'DESC' },
    },
    { enabled: !!record?.id },
  );

  if (!record) return null;

  const quote = quotes?.[0];

  const updateQuoteField = async (quoteId: string, field: string, value: string) => {
    try {
      const { error } = await supabase
        .from('quotes')
        .update({ [field]: value || null })
        .eq('id', quoteId);
      if (error) throw error;
      notify('Updated', { type: 'success' });
      refresh();
    } catch (e: any) {
      notify(e.message || 'Failed to update', { type: 'warning' });
    }
  };

  const formatDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  const intentLabel = (intent: string | null) => {
    if (!intent) return '—';
    if (intent.includes('pre')) return 'Pre-Need';
    if (intent.includes('at')) return 'At-Need';
    return intent;
  };

  const leadTypeLabel = (lt: string | null) => {
    if (!lt) return '—';
    return lt.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
  };

  const startEditContact = () => {
    setContactForm({
      first_name: contact?.first_name || '',
      last_name: contact?.last_name || '',
      primary_phone: contact?.phone_number || '',
      primary_email: contact?.email || '',
    });
    setEditingContact(true);
  };

  const saveContact = async () => {
    try {
      const displayName = [contactForm.first_name, contactForm.last_name].filter(Boolean).join(' ').trim();
      const { error } = await supabase
        .from('clients')
        .update({
          first_name: contactForm.first_name.trim() || null,
          last_name: contactForm.last_name.trim() || null,
          display_name: displayName || contact?.name || null,
          phone_number: contactForm.primary_phone.trim() || null,
          email: contactForm.primary_email.trim().toLowerCase() || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', record.client_id);
      if (error) throw error;
      notify('Contact updated', { type: 'success' });
      setEditingContact(false);
      refresh();
    } catch (e: any) {
      notify(e.message || 'Failed to update contact', { type: 'warning' });
    }
  };

  return (
    <div className="bg-background flex flex-col h-full min-h-0">
      <ScrollArea className="h-full">
        <div className="pb-6 pt-1">
          <Accordion
            type="multiple"
            key={sidebarKey}
            defaultValue={['details', 'contact', 'additional_contacts', 'quote', 'attribution']}
          >
            {/* Details */}
            <AccordionItem value="details">
              <AccordionTrigger icon={<FileText className="w-4 h-4" />}>
                Details
              </AccordionTrigger>
              <AccordionContent>
                <Row label="Stage"><StageChip stage={record.stage} /></Row>
                <Row label="Type">
                  <InlineSelect
                    value={quote?.deal_type || (record.intent?.includes('pre') ? 'pre_need' : record.intent?.includes('at') ? 'at_need' : null)}
                    options={[
                      { value: 'at_need', label: 'At-Need' },
                      { value: 'pre_need', label: 'Pre-Need' },
                    ]}
                    placeholder="—"
                    onChange={async (val) => {
                      try {
                        if (quote) {
                          await updateQuoteField(quote.id, 'deal_type', val);
                        } else {
                          const leadIntent = val === 'at_need' ? 'at-need' : 'pre-need';
                          const { error } = await supabase
                            .from('leads')
                            .update({ intent: leadIntent, updated_at: new Date().toISOString() })
                            .eq('id', record.id);
                          if (error) throw error;
                          notify('Updated', { type: 'success' });
                          refresh();
                        }
                      } catch (e: any) {
                        notify(e.message || 'Failed to update', { type: 'warning' });
                      }
                    }}
                  />
                </Row>
                <Row label="Source">
                  <span className="capitalize">{record.source_type || '—'}</span>
                  {record.source_channel && (
                    <span className="text-muted-foreground"> ({record.source_channel})</span>
                  )}
                </Row>
                <Row label="Language">{record.language?.toUpperCase() || '—'}</Row>
                <Row label="Opened">{formatDate(record.opened_at)}</Row>
                <Row label="Qualified">{formatDate(record.qualified_at)}</Row>
                <Row label="Lead Type">{leadTypeLabel(record.lead_type)}</Row>
                {record.closed_lost_reason && (
                  <Row label="Lost Reason">
                    <span className="text-red-600 capitalize">{record.closed_lost_reason.replace(/_/g, ' ')}</span>
                  </Row>
                )}
                {!['closed_won', 'closed_lost', 'qualified', 'disqualified'].includes(record.stage) && (
                  <div className="pt-2 flex items-center gap-2">
                    <button
                      onClick={() => setQualifyOpen(true)}
                      className="text-[13px] text-green-600 hover:text-green-700 font-medium border border-green-300 rounded px-2.5 py-1 hover:bg-green-50 transition-colors"
                    >
                      Qualify
                    </button>
                    <LeadDisqualifyButton />
                    <LeadLinkButton leadId={String(record.id)} />
                  </div>
                )}
                <LeadLinkedBanner leadId={String(record.id)} stage={record.stage} />
                {['qualified', 'disqualified', 'closed_lost', 'spam'].includes(record.stage) && (
                  <div className="pt-2 flex items-center gap-2">
                    <LeadCloseLostButton open={closeLostOpen} onOpenChange={setCloseLostOpen} />
                    <LeadResetStageButton
                      linkedFuneralId={quote?.funeral_id}
                      onCloseLost={() => setCloseLostOpen(true)}
                    />
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* Contact (editable) */}
            <AccordionItem value="contact">
              <AccordionTrigger icon={<User className="w-4 h-4" />}>
                <span className="flex items-center gap-2">
                  Contact
                  {!editingContact && (
                    <Pencil
                      className="w-3 h-3 text-muted-foreground hover:text-primary cursor-pointer"
                      onClick={(e) => { e.stopPropagation(); startEditContact(); }}
                    />
                  )}
                </span>
              </AccordionTrigger>
              <AccordionContent>
                {editingContact ? (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        className="text-[13px] border border-border rounded px-2 py-1 flex-1 bg-background"
                        placeholder="First name"
                        value={contactForm.first_name}
                        onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
                      />
                      <input
                        className="text-[13px] border border-border rounded px-2 py-1 flex-1 bg-background"
                        placeholder="Last name"
                        value={contactForm.last_name}
                        onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })}
                      />
                    </div>
                    <input
                      className="text-[13px] border border-border rounded px-2 py-1 w-full bg-background"
                      placeholder="Phone"
                      value={contactForm.primary_phone}
                      onChange={(e) => setContactForm({ ...contactForm, primary_phone: e.target.value })}
                    />
                    <input
                      className="text-[13px] border border-border rounded px-2 py-1 w-full bg-background"
                      placeholder="Email"
                      value={contactForm.primary_email}
                      onChange={(e) => setContactForm({ ...contactForm, primary_email: e.target.value })}
                    />
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        onClick={saveContact}
                        className="text-[13px] text-white bg-primary hover:bg-primary/90 rounded px-2.5 py-1 font-medium"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingContact(false)}
                        className="text-[13px] text-muted-foreground hover:text-foreground"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <ContactCard
                    contact={{
                      name: contact?.name || '—',
                      phone: contact?.phone_number,
                      email: contact?.email,
                      clientId: contact?.id ? Number(contact.id) : undefined,
                    }}
                    context={{ leadId: record?.id ? String(record.id) : undefined }}
                    renderName={() => (
                      <button
                        type="button"
                        onClick={startEditContact}
                        className="font-medium hover:underline"
                        title="Edit contact"
                      >
                        {contact?.name || '—'}
                      </button>
                    )}
                    renderEdit={() => (
                      <button
                        type="button"
                        onClick={startEditContact}
                        className="inline-flex items-center gap-1 px-2 h-7 rounded-md text-[12px] text-muted-foreground hover:text-primary hover:bg-muted"
                        title="Edit contact"
                      >
                        <Pencil className="w-4 h-4" /> Edit
                      </button>
                    )}
                  />
                )}
              </AccordionContent>
            </AccordionItem>

            {/* Additional Contacts */}
            {quote && (
              <AccordionItem value="additional_contacts">
                <AccordionTrigger icon={<UserPlus className="w-4 h-4" />}>
                  <span className="flex items-center gap-2">
                    Additional Contacts
                    <button
                      onClick={(e) => { e.stopPropagation(); setAddContactOpen(true); }}
                      className="text-[11px] text-primary hover:text-primary/80 border border-primary/30 rounded px-1.5 py-0.5 hover:bg-primary/5"
                    >
                      + Add
                    </button>
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <LinkedContactsList quoteId={quote.id} />
                </AccordionContent>
              </AccordionItem>
            )}

            {/* Quote (conditional) */}
            {quote && (
              <AccordionItem value="quote">
                <AccordionTrigger icon={<Zap className="w-4 h-4" />}>
                  Quote
                </AccordionTrigger>
                <AccordionContent>
                  <Row label="Timeline">
                    <InlineSelect
                      value={quote.timeline}
                      options={quote.deal_type === 'pre_need' ? [
                        { value: 'Planning now', label: 'Planning now' },
                        { value: 'Follow up', label: 'Follow up' },
                      ] : [
                        { value: 'Passed', label: 'Passed' },
                        { value: 'Imminent (1-7 days)', label: 'Imminent (1-7 days)' },
                        { value: 'Near future (7-30 days)', label: 'Near future (7-30 days)' },
                        { value: 'Preplanning (30+ days)', label: 'Preplanning (30+ days)' },
                      ]}
                      placeholder="—"
                      onChange={(val) => updateQuoteField(quote.id, 'timeline', val)}
                    />
                  </Row>
                  {quote.quoted_price && (
                    <Row label="Price">${Number(quote.quoted_price).toLocaleString()}</Row>
                  )}
                  <Row label="Quote">
                    <a href={`/quotes/${quote.id}/show`} target="_blank" className="text-primary hover:underline">
                      View
                    </a>
                  </Row>
                  {quote.funeral_id && (
                    <Row label="Funeral">
                      <a href={`/funerals/${quote.funeral_id}/show`} target="_blank" className="text-primary hover:underline">
                        #{quote.funeral_id}
                      </a>
                    </Row>
                  )}
                </AccordionContent>
              </AccordionItem>
            )}

            {/* Attribution (conditional) */}
            {(record.gclid || record.utm_campaign || record.utm_source) && (
              <AccordionItem value="attribution">
                <AccordionTrigger icon={<Globe className="w-4 h-4" />}>
                  Attribution
                </AccordionTrigger>
                <AccordionContent>
                  {record.gclid && <Row label="GCLID"><span className="font-mono text-[11px] break-all">{record.gclid}</span></Row>}
                  {record.utm_campaign && <Row label="Campaign">{record.utm_campaign}</Row>}
                  {record.utm_source && <Row label="Source">{record.utm_source}</Row>}
                  {record.utm_medium && <Row label="Medium">{record.utm_medium}</Row>}
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </div>
      </ScrollArea>

      <QualifyLeadDialog
        open={qualifyOpen}
        onClose={() => { setQualifyOpen(false); refreshSidebar(); }}
        leadId={String(record.id)}
        contact={contact || null}
        lead={record}
      />

      {quote && (
        <AddContactDialog
          open={addContactOpen}
          onClose={() => { setAddContactOpen(false); refreshSidebar(); }}
          quoteId={quote.id}
          leadId={String(record.id)}
        />
      )}
    </div>
  );
}

// ─── Activity feed (main content area) ───────────────────────────────────────

function CopyClientLinkButton({ quoteId }: { quoteId: string }) {
  const notify = useNotify();
  const [copied, setCopied] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const url = `https://cleocremation.com/app/${quoteId}`;

  const handleCopy = () => {
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      notify('Client link copied', { type: 'success' });
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Open the client link with ?staff=1 so aura-app suppresses the page-view
  // timeline events while staff is previewing.
  const handlePreview = () => {
    setMenuAnchor(null);
    window.open(`${url}?staff=1`, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <ButtonGroup size="small" variant="outlined" color="primary">
        <Button
          onClick={handleCopy}
          sx={{ textTransform: 'none', fontSize: 12, py: 0.25, px: 1 }}
        >
          {copied ? 'Copied!' : 'Copy Client Link'}
        </Button>
        <Button
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{ minWidth: 0, px: 0.5 }}
          title="More link options"
        >
          <ArrowDropDownIcon sx={{ fontSize: 18 }} />
        </Button>
      </ButtonGroup>
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={handlePreview} sx={{ fontSize: 13 }}>Preview Link</MenuItem>
      </Menu>
    </>
  );
}

function LeadActivityFeed({ onMenuClick }: { onMenuClick?: () => void } = {}) {
  const record = useRecordContext();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [optimisticMessages, setOptimisticMessages] = useState<CustomerEvent[]>([]);

  // Primary contact
  const { data: primaryContact } = useGetOne(
    'clients',
    { id: record?.client_id },
    { enabled: !!record?.client_id },
  );

  // Quote (for quote_contacts lookup)
  const { data: quotes } = useGetList(
    'quotes',
    {
      filter: { lead_id: record?.id },
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'created_at', order: 'DESC' },
    },
    { enabled: !!record?.id },
  );
  const quote = quotes?.[0];

  // Additional contacts via quote_contacts
  const { data: quoteContacts } = useGetList(
    'quote_contacts',
    {
      filter: { quote_id: quote?.id },
      pagination: { page: 1, perPage: 50 },
      sort: { field: 'created_at', order: 'ASC' },
    },
    { enabled: !!quote?.id },
  );

  // Gather all unique contact IDs
  const additionalContactIds = useMemo(() => {
    if (!quoteContacts) return [];
    return quoteContacts
      .map((qc) => qc.client_id)
      .filter((id) => id !== record?.client_id);
  }, [quoteContacts, record?.client_id]);

  // Fetch additional contact records
  const { data: additionalContacts } = useGetMany(
    'clients',
    { ids: additionalContactIds },
    { enabled: additionalContactIds.length > 0 },
  );

  // Build unified contact list with colors
  const allContacts = useMemo(() => {
    const contacts: { id: string; name: string; color: string; phone_number: string; email: string | null }[] = [];

    if (primaryContact) {
      contacts.push({
        id: primaryContact.id,
        name: primaryContact.name || [primaryContact.first_name, primaryContact.last_name].filter(Boolean).join(' ') || 'Primary',
        color: getClientColor(0),
        phone_number: primaryContact.phone_number || primaryContact.mobile_phone_number || '',
        email: primaryContact.email || null,
      });
    }

    if (additionalContacts) {
      additionalContacts.forEach((c, i) => {
        if (c && !contacts.some((existing) => existing.id === c.id)) {
          contacts.push({
            id: c.id,
            name: c.name || [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Contact',
            color: getClientColor(i + 1),
            phone_number: c.phone_number || c.mobile_phone_number || '',
            email: c.email || null,
          });
        }
      });
    }

    return contacts;
  }, [primaryContact, additionalContacts]);

  // Contact filter state
  const [activeContactIds, setActiveContactIds] = useState<Set<string>>(new Set());

  // Initialize active contacts when data loads
  useEffect(() => {
    if (allContacts.length > 0 && activeContactIds.size === 0) {
      setActiveContactIds(new Set(allContacts.map((c) => c.id)));
    }
  }, [allContacts, activeContactIds.size]);

  const toggleContact = useCallback((contactId: string) => {
    setActiveContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  }, []);

  // Filtered contact IDs for the feed
  const filteredClientIds = useMemo(
    () => allContacts.filter((c) => activeContactIds.has(c.id)).map((c) => Number(c.id)),
    [allContacts, activeContactIds],
  );

  // Map contacts to MessageInputBar client shape (uses number ids for clientId, but we send by phone)
  // We use a numeric hash of the contact UUID as the "id" for the recipient picker
  const messageBarClients = useMemo(
    () => allContacts.filter((c) => c.phone_number).map((c, i) => ({
      id: i, // index-based id for the picker
      name: c.name,
      color: c.color,
      phone_number: c.phone_number,
      email: c.email,
    })),
    [allContacts],
  );

  const handleMessageSent = useCallback((event: CustomerEvent) => {
    setOptimisticMessages((prev) => [...prev, event]);
    // Auto-scroll to bottom
    setTimeout(() => {
      if (scrollRef.current) {
        const scrollEl = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
        if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
      }
    }, 50);
  }, []);

  if (!record) return null;

  const contactName = primaryContact?.name || 'Lead';
  const hasAnyPhone = allContacts.some((c) => c.phone_number);

  return (
    <div className="cleo-ui flex flex-col h-full bg-background font-sans overflow-x-hidden">
      {/* Header */}
      <div className="px-3 sm:px-6 py-2 sm:py-4 border-b flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-base sm:text-lg font-semibold truncate">{contactName}</h1>
          {primaryContact?.phone_number && (
            <p className="text-xs sm:text-sm text-muted-foreground truncate">{primaryContact.phone_number}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {quote && <CopyClientLinkButton quoteId={quote.id} />}
          {onMenuClick && (
            <button onClick={onMenuClick} className="p-1.5 rounded-md hover:bg-muted transition-colors">
              <MenuLucide className="h-5 w-5 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* Contact filter bar */}
      {allContacts.length >= 2 && (
        <div className="flex items-center gap-2 px-3 sm:px-4 py-2 border-b shrink-0 overflow-x-auto">
          {allContacts.map((contact) => {
            const isActive = activeContactIds.has(contact.id);
            return (
              <button
                key={contact.id}
                onClick={() => toggleContact(contact.id)}
                className="rounded-full px-3 py-1 text-xs font-medium transition-colors border shrink-0 whitespace-nowrap"
                style={
                  isActive
                    ? { backgroundColor: contact.color, borderColor: contact.color, color: '#fff' }
                    : { backgroundColor: 'transparent', borderColor: contact.color, color: contact.color }
                }
              >
                {contact.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Feed */}
      <div className="flex-1 flex flex-col min-h-0" ref={scrollRef}>
        <ScrollArea className="flex-1 min-h-0">
          {filteredClientIds.length > 0 ? (
            <CallLinkProvider>
              <ContactActivityFeed
                clientIds={filteredClientIds}
                leadId={String(record.id)}
                optimisticEvents={optimisticMessages}
              />
            </CallLinkProvider>
          ) : (
            <div className="flex items-center justify-center h-40 text-sm text-muted-foreground">
              No activity yet
            </div>
          )}
        </ScrollArea>
      </div>

      {/* The qualify follow-up text, until it goes (T-190) */}
      <ScheduledSmsRow leadId={String(record.id)} />

      {/* Message input */}
      {(hasAnyPhone || record.id) && (
        <MessageInputBar
          clients={messageBarClients}
          defaultRecipientId={messageBarClients.length > 0 ? messageBarClients[0].id : null}
          leadId={String(record.id)}
          onMessageSent={handleMessageSent}
        />
      )}
    </div>
  );
}

// ─── Main Show component ─────────────────────────────────────────────────────

// ─── Drawer version (for inline use in analytics) ────────────────────────────

function DrawerSkeleton() {
  return (
    <div className="cleo-ui flex flex-col h-full animate-pulse">
      <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30 shrink-0">
        <div className="h-4 w-24 bg-muted rounded" />
        <div className="flex gap-1">
          <div className="h-7 w-7 bg-muted rounded" />
          <div className="h-7 w-7 bg-muted rounded" />
        </div>
      </div>
      <div className="flex flex-1 min-h-0">
        <div className="w-72 border-r p-4 space-y-4">
          <div className="h-4 w-16 bg-muted rounded" />
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex justify-between">
                <div className="h-3 w-16 bg-muted rounded" />
                <div className="h-3 w-20 bg-muted rounded" />
              </div>
            ))}
          </div>
          <div className="h-4 w-16 bg-muted rounded mt-6" />
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex justify-between">
                <div className="h-3 w-14 bg-muted rounded" />
                <div className="h-3 w-24 bg-muted rounded" />
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1 p-6 space-y-4">
          <div className="h-5 w-40 bg-muted rounded" />
          <div className="h-3 w-28 bg-muted rounded" />
          <div className="mt-8 space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 bg-muted rounded-lg w-3/4 mx-auto" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function LeadDrawer({
  leadId,
  open,
  onClose,
}: {
  leadId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const isMobile = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { data: lead, isLoading } = useGetOne(
    'leads',
    { id: leadId! },
    { enabled: !!leadId },
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      disableEnforceFocus
      sx={{
        '& .MuiDrawer-paper': {
          // Stay clear of the inbox. `--inbox-dock-w` is how much of the
          // screen the dock has taken (0 when it is closed or floating), so
          // the drawer both shifts left by it and gives back the same width
          // instead of running off the edge. Set here rather than as a global
          // CSS override so it carries `sx` specificity and can't be lost.
          width: isMobile
            ? '100vw'
            : 'min(calc(95vw - var(--inbox-dock-w, 0px)), 1100px)',
          right: isMobile ? 0 : 'var(--inbox-dock-w, 0px)',
        },
      }}
    >
      {isLoading || !lead ? (
        <DrawerSkeleton />
      ) : (
        <RecordContextProvider value={lead}>
          <MessageDraftProvider>
          <div className="cleo-ui flex flex-col h-full">
            {/* Header bar */}
            <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30 shrink-0">
              <span className="text-sm font-medium text-muted-foreground">Lead Details</span>
              <div className="flex items-center gap-1">
                <LeadOwnerControls />
                {isMobile && (
                  <IconButton
                    size="small"
                    onClick={() => setSidebarOpen(true)}
                    title="Show details"
                  >
                    <MenuLucide className="h-4 w-4" />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  href={`/leads/${leadId}/show`}
                  target="_blank"
                  title="Open in new tab"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={onClose} title="Close">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            </div>
            {/* Content: feed + sidebar side by side (sidebar collapses on mobile) */}
            <div className="flex flex-1 min-h-0">
              <div className="flex-1 min-w-0 overflow-hidden">
                <LeadActivityFeed />
              </div>
              {!isMobile && (
                <div className="shrink-0 border-l overflow-y-auto" style={{ width: 340 }}>
                  <LeadShowSidebar />
                </div>
              )}
            </div>
            {isMobile && (
              <Drawer
                anchor="right"
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                disableEnforceFocus
                PaperProps={{ sx: { width: '85vw', maxWidth: 360 } }}
              >
                <div className="cleo-ui h-full overflow-y-auto">
                  <LeadShowSidebar />
                </div>
              </Drawer>
            )}
          </div>
          </MessageDraftProvider>
        </RecordContextProvider>
      )}
    </Drawer>
  );
}

// ─── Main Show component (full page) ─────────────────────────────────────────

function LeadShowContent() {
  const isXSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'));
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (isXSmall) {
    return (
      <>
        <LeadActivityFeed onMenuClick={() => setSidebarOpen(true)} />
        <Drawer
          anchor="right"
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          PaperProps={{ sx: { width: '85vw', maxWidth: 360 } }}
        >
          <div className="cleo-ui h-full">
            <LeadShowSidebar />
          </div>
        </Drawer>
      </>
    );
  }

  return <LeadActivityFeed />;
}

export const LeadShow = () => {
  const isXSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'));

  return (
    <MessageDraftProvider>
    <Show
      resource="leads"
      actions={isXSmall ? false : <LeadShowToolBar />}
      aside={
        !isXSmall ? (
          <div
            className="cleo-ui"
            style={{
              width: 384,
              height: 'calc(100vh - 128px)',
              borderRadius: '8px',
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
              border: '1px solid rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            <LeadShowSidebar />
          </div>
        ) : undefined
      }
      sx={
        isXSmall
          ? {
              '& .RaShow-main': { maxWidth: '100%', display: 'flex', padding: 0 },
              '& .RaShow-card': {
                boxShadow: 'none',
                borderRadius: 0,
                overflow: 'hidden',
                flex: 1,
                border: 'none',
                height: 'calc(100vh - 48px)',
                display: 'flex',
                flexDirection: 'column',
              },
              '& .RaShow-card > .MuiCardContent-root': {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                padding: 0,
                '&:last-child': { paddingBottom: 0 },
              },
            }
          : {
              '& .RaShow-main': {
                maxWidth: '100%',
                display: 'flex',
                gap: '16px',
                padding: '16px 16px 16px 0',
              },
              '& .RaShow-card': {
                boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
                borderRadius: '8px',
                overflow: 'hidden',
                flex: 1,
                border: '1px solid rgba(0,0,0,0.08)',
                height: 'calc(100vh - 128px)',
                display: 'flex',
                flexDirection: 'column',
              },
              '& .RaShow-card > .MuiCardContent-root': {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                padding: 0,
                '&:last-child': { paddingBottom: 0 },
              },
              '& .RaShow-aside': {
                position: 'sticky',
                top: 16,
                alignSelf: 'flex-start',
              },
            }
      }
    >
      <LeadShowContent />
    </Show>
    </MessageDraftProvider>
  );
};
